import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/cn';

/**
 * An on/off switch — a pill with a sliding thumb, 52 × 32 as on the phone.
 *
 * This was a bare `<input type="checkbox">`, which draws a square tick box: it
 * reads as "tick this to agree" rather than "this is on", and next to a filled
 * primary button it looked like an unfinished form.
 *
 * A button with role="switch", not a checkbox input, so it announces on/off
 * and works from the keyboard for free. The thumb's travel flips with the
 * language, as Namat's does — a thumb that slides left in a right-to-left
 * layout points the wrong way.
 */
export function Toggle({
  testId, checked, onChange, label, disabled,
}: {
  testId: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  const { dir } = useLanguage();
  const isRTL = dir === 'rtl';

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      data-testid={testId}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'inline-flex h-8 w-[52px] shrink-0 cursor-pointer items-center rounded-full p-0.5 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-ground',
        'disabled:cursor-not-allowed disabled:opacity-50',
        // On is the main-action colour (teal on paper, lime on night); off is
        // the control border, which reads at 3:1 on both grounds.
        checked ? 'bg-primary' : 'bg-border-strong',
      )}
    >
      <span
        data-testid={`${testId}-thumb`}
        className={cn(
          'pointer-events-none block h-7 w-7 rounded-full shadow-md ring-0 transition-transform',
          checked ? 'bg-card dark:bg-primary-foreground' : 'bg-card dark:bg-ink',
          checked ? (isRTL ? '-translate-x-5' : 'translate-x-5') : 'translate-x-0',
        )}
      />
    </button>
  );
}
